import type { DataSource } from "@firecms/core";
import { getApp } from "firebase/app";
import { doc, getDoc, getFirestore } from "firebase/firestore";

import { eventTaskPath } from "./eventTask";
import { buildEventsPath, eventsCollection, type Event } from "./events";
import type { EventStatus } from "../properties/eventStatus";
import {
  eventStatusDenialMessage,
  isEventManagerAccessible,
} from "../properties/eventStatus";
import type { Locale } from "../localization/constants";

export type EventRuntimeState = {
  status?: EventStatus;
  /** Error from `tasks/provisioning` when that run failed. */
  error?: string;
};

type DataSourceFetcher = Pick<DataSource, "fetchEntity">;

export async function fetchEventRuntimeState(
  dataSource: DataSourceFetcher,
  accountId: string,
  eventId: string
): Promise<EventRuntimeState | undefined> {
  const entity = await dataSource.fetchEntity<Event>({
    path: buildEventsPath({ accountId }),
    collection: eventsCollection,
    entityId: eventId,
  });
  if (!entity?.values) {
    return undefined;
  }

  let error: string | undefined;
  try {
    const snap = await getDoc(
      doc(getFirestore(getApp()), eventTaskPath(accountId, eventId, "provisioning"))
    );
    const raw = snap.data()?.error;
    if (typeof raw === "string" && raw.length > 0) {
      error = raw;
    }
  } catch {
    error = undefined;
  }

  return {
    status: entity.values.status as EventStatus | undefined,
    error,
  };
}

export function isEventProvisioningReady(status: EventStatus | undefined): boolean {
  return isEventManagerAccessible(status);
}

export function provisioningDenialMessage(
  state: EventRuntimeState | undefined,
  locale?: Locale
): string {
  return eventStatusDenialMessage(state?.status, state?.error, locale);
}
